import { useCallback, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { hasPermission, isAdminUser } from '../utils/permissionControl';
import {
  clearRescueSearchDraftTargetMarker,
  placeRescueSearchDraftTargetMarker
} from '../utils/dashboard/rescueSearchTempMarker';

function canPushRescueTarget() {
  return isAdminUser() || hasPermission('救援推送');
}

/**
 * 指挥页搜索坐标 → 正式打点 → 点水滴打开「目标推送」面板
 */
export default function useDashboardRescueSearchTargetPush({
  getMap,
  displayRescueTargetOnMap,
  loadRescueData
}) {
  const draftHolderRef = useRef({});
  const [pushPoint, setPushPoint] = useState(null);

  const clearDraft = useCallback(() => {
    clearRescueSearchDraftTargetMarker(draftHolderRef.current);
  }, []);

  const openPushAt = useCallback((lat, lng) => {
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) {
      message.warning('没有可用坐标');
      return;
    }
    setPushPoint({ lat: la, lng: ln });
  }, []);

  const plantRescueSearchTarget = useCallback((lat, lng) => {
    if (!canPushRescueTarget()) {
      message.warning('无「救援推送」权限，无法设置为目标位置');
      return;
    }
    const map = typeof getMap === 'function' ? getMap() : null;
    if (!map) {
      message.warning('地图未就绪');
      return;
    }
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln) || Math.abs(la) > 90 || Math.abs(ln) > 180) {
      message.warning('没有可用坐标');
      return;
    }

    placeRescueSearchDraftTargetMarker(map, la, ln, {
      holder: draftHolderRef.current,
      onOpenPush: openPushAt,
      fly: true,
      title: '待推送目标'
    });
    message.success('已在地图打点，点击水滴可推送给队员');
  }, [getMap, openPushAt]);

  const closePushModal = useCallback(() => {
    setPushPoint(null);
  }, []);

  const handlePushed = useCallback((target) => {
    if (target) {
      try {
        displayRescueTargetOnMap?.(target);
      } catch (e) {
        console.warn('上图救援目标失败', e);
      }
      try {
        loadRescueData?.();
      } catch (_) {
        /* ignore */
      }
    }
    clearDraft();
  }, [clearDraft, displayRescueTargetOnMap, loadRescueData]);

  return {
    plantRescueSearchTarget,
    clearRescueSearchDraftTarget: clearDraft,
    rescueSearchTargetPushModalProps: {
      open: !!pushPoint,
      lat: pushPoint?.lat,
      lng: pushPoint?.lng,
      getMap,
      onClose: closePushModal,
      onPushed: handlePushed,
      onClearDraftTargets: clearDraft
    }
  };
}
